/**
 * MobileLiveScoring — Mobile-native live scoring page
 * 4 tabs: My Team | Standings | Games | All Teams
 * Auto-refreshes every 30s. Gamecast inline on game tap.
 * No desktop imports. Uses existing live scoring endpoints.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { dynastyTheme, dynastyTileStyle, withAlpha } from '../../../services/colorService';
import { mobileTableStyles, mobileColors } from '../../../services/mobileStyleService';

import { leaguesAPI } from '../../../services/apiService';
import { MobilePlayerModal, MobilePlayerCard } from '../components/player';
import MobilePlayerLink from '../components/MobilePlayerLink';
// Bible § 14: canonical defaults for league-scoring-driven column sets.
import { DEFAULT_BATTING_STATS, DEFAULT_PITCHING_STATS } from '../../../utils/statMapping';
import { isPitcherSlot, isPitcherPosition } from '../../../utils/playerClassification';
// DD-STAT-CONSOLIDATION: shared SSoT for the live-scoring locked rule (same logic as
// desktop LiveScoring). Player rate columns = PURE SEASON (from emitted components);
// totals row = DAY throughout.
import {
  seasonRate,
  formatRate,
  buildLiveScoringColumns,
  HITTING_CONTEXT,
  PITCHING_CONTEXT,
} from '../../../services/tables/liveScoringRates';

const C = dynastyTheme.tokens.colors;

const REFRESH_INTERVAL = 5000;

// ============================================================================
// HELPERS
// ============================================================================
const hideScrollStyle = {
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
};

const StatusDot = ({ status }) => {
  if (!status || status === 'S') return null;
  const color = (status === 'I') ? dynastyTheme.tokens.colors.success : dynastyTheme.tokens.colors.primary;
  return <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color, display: 'inline-block', marginRight: 4, verticalAlign: 'middle' }} />;
};

// Game-card status dot: live = green solid, final = gold solid, upcoming = open
// gray dashed ring (spec: green/gold/open-dashed by state).
const GameDot = ({ statusCode }) => {
  const base = { width: 7, height: 7, borderRadius: '50%', display: 'inline-block', marginRight: 4, verticalAlign: 'middle' };
  if (statusCode === 'I') return <span style={{ ...base, backgroundColor: dynastyTheme.tokens.colors.success }} />;
  if (statusCode === 'F' || statusCode === 'O') return <span style={{ ...base, backgroundColor: dynastyTheme.tokens.colors.primary }} />;
  return <span style={{ ...base, backgroundColor: 'transparent', border: `1px dashed ${C.muted}` }} />;
};

// Locked ownership chip (HANDOFF global rule 7) — VIBRANT text on a low-opacity
// tint of the same hue, per Live Scoring.dc.html. YOURS gold / team-abbr blue
// #60a5fa / FA green #10b981 (not the muted token blue #3b82f6 / faint alpha).
const BADGE_BASE = { fontSize: 8, fontWeight: 700, padding: '1px 4px', borderRadius: 2, whiteSpace: 'nowrap' };
const OwnerBadge = ({ team, isYours }) => {
  if (isYours) return <span style={{ ...BADGE_BASE, backgroundColor: C.primaryTint.t13, color: dynastyTheme.tokens.colors.primary }}>YOURS</span>;
  if (team) return <span style={{ ...BADGE_BASE, backgroundColor: C.ownBadge.teamBg, color: C.blue }}>{team.substring(0, 10)}</span>;
  return <span style={{ ...BADGE_BASE, backgroundColor: C.ownBadge.faBg, color: C.success }}>FA</span>;
};

const getGameStatusText = (game) => {
  if (!game) return '';
  if (game.status_code === 'I') return `${game.inning_state || ''} ${game.inning || ''}`.trim();
  if (game.status_code === 'F' || game.status_code === 'O') return 'Final';
  return game.game_time || 'TBD';
};

const formatStat = (val) => {
  if (val === '-' || val === null || val === undefined) return '-';
  if (typeof val === 'number') {
    if (val % 1 !== 0 && val < 2) return val.toFixed(3).replace(/^0/, '');
    return val;
  }
  return val;
};

// ============================================================================
// TAB 1: MY TEAM
// ============================================================================
const MyTeamTab = ({ leagueId, allTeams, userTeamId, onPlayerClick, onOpenGame, refreshKey, overrideTeamId }) => {
  const [subTab, setSubTab] = useState('hitting');
  const [selectedTeamId, setSelectedTeamId] = useState(userTeamId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { setSelectedTeamId(userTeamId); }, [userTeamId]);
  useEffect(() => { if (overrideTeamId?.teamId) setSelectedTeamId(overrideTeamId.teamId); }, [overrideTeamId]);

  useEffect(() => {
    if (!leagueId) return;
    setLoading(true);
    leaguesAPI.getLiveScoringTeamStats(leagueId, selectedTeamId)
      .then(res => setData(res))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [leagueId, selectedTeamId, refreshKey]);

  if (loading && !data) return <div style={{ padding: 20, textAlign: 'center', color: dynastyTheme.tokens.colors.neutral[500], fontSize: 12 }}>Loading stats...</div>;
  if (!data) return <div style={{ padding: 20, textAlign: 'center', color: dynastyTheme.tokens.colors.neutral[500], fontSize: 12 }}>No data available</div>;

  const players = subTab === 'hitting' ? (data.hitting || []) : (data.pitching || []);
  const totals = subTab === 'hitting' ? data.hitter_totals : data.pitcher_totals;

  // DD-STAT-CONSOLIDATION Item 1: columns via the shared SSoT builder (registry
  // canonical order, one column source) — identical treatment to desktop. The
  // team-stats endpoint carries no league categories, so the default scoring sets
  // are used (unchanged from the prior hardcoded set; now canonical-ordered).
  const statType = subTab === 'hitting' ? 'hitting' : 'pitching';
  const contextKeys = subTab === 'hitting' ? HITTING_CONTEXT : PITCHING_CONTEXT;
  const scoringCats = subTab === 'hitting'
    ? ['R', 'HR', 'RBI', 'SB', 'AVG', 'OPS']
    : ['W', 'SV', 'QS', 'SO', 'ERA', 'WHIP'];
  const identityCols = [
    { key: 'name', label: 'Player', sticky: true },
    { key: 'mlb_team', label: 'Tm', ctx: true },
    { key: 'game_status', label: 'Game', ctx: true },
  ];
  const cols = [
    ...identityCols,
    ...buildLiveScoringColumns(scoringCats, statType, contextKeys).map((c) => ({
      key: c.liveKey,
      label: c.label,
      abbrev: c.abbrev,
      isRate: c.isRate,
      ctx: !c.isScoring,
      scoring: c.isScoring,
    })),
  ];

  const getStatusCode = (gs) => {
    if (!gs) return 'S';
    if (gs.includes('Final') || gs.includes('final')) return 'F';
    if (gs.includes('Top') || gs.includes('Bot') || gs.includes('Mid') || gs.includes('End')) return 'I';
    return 'S';
  };

  // Determine if player has appeared in today's game (has non-zero counting stats)
  const hasAppeared = (p) => {
    if (subTab === 'hitting') {
      return (typeof p.ab === 'number' && p.ab > 0) || (typeof p.bb === 'number' && p.bb > 0) || (typeof p.hbp === 'number' && p.hbp > 0);
    }
    // Pitching: check IP
    const ip = parseFloat(p.ip);
    return !isNaN(ip) && ip > 0;
  };

  // Green = currently active, gold = appeared but exited or game final
  const getDotStatus = (p) => {
    const sc = getStatusCode(p.game_status);
    if (!hasAppeared(p)) return null;
    // Bible §4.5.9.1: classify by occupied slot (feed carries roster_position).
    const isPitcher = p.roster_position
      ? isPitcherSlot(p.roster_position)
      : isPitcherPosition(p.position);
    if (sc === 'I') {
      // For pitchers: green only if still on mound, gold if exited
      if (isPitcher) return p.is_current_pitcher ? 'I' : 'F';
      return 'I';
    }
    if (sc === 'F') return 'F';
    return null;
  };

  // Locked "grouped My Team": partition by game status into Live / Final /
  // Upcoming, each under a subheader (● green live / ◐ gold final / ○ upcoming).
  const STATUS_GROUPS = [
    { code: 'I', label: 'Live', dot: dynastyTheme.tokens.colors.success, glyph: '●' },
    { code: 'F', label: 'Final', dot: dynastyTheme.tokens.colors.primary, glyph: '◐' },
    { code: 'S', label: 'Upcoming', dot: dynastyTheme.tokens.colors.neutral[500], glyph: '○' },
  ];
  const grouped = STATUS_GROUPS
    .map((g) => ({ ...g, rows: players.filter((p) => getStatusCode(p.game_status) === g.code) }))
    .filter((g) => g.rows.length > 0);

  // One player row (extracted so it renders identically inside each status group).
  const renderPlayerRow = (p, idx) => {
    const sc = getStatusCode(p.game_status);
    const dotStatus = getDotStatus(p);
    const rowBg = idx % 2 === 0 ? mobileTableStyles.rows.even : mobileTableStyles.rows.odd;
    return (
      <tr key={p.player_id || idx} style={{ backgroundColor: rowBg }}>
        {cols.map(col => {
          // +10% fonts (10→11); non-player cells tightened to 3px 2px.
          const base = { padding: '3px 2px', textAlign: col.sticky ? 'left' : 'center', whiteSpace: 'nowrap', fontSize: 11 };
          if (col.sticky) {
            return (
              <td key={col.key} style={{ ...base, position: 'sticky', left: 0, zIndex: 1, backgroundColor: rowBg, padding: '4px 6px', width: 112, minWidth: 112, maxWidth: 112, overflow: 'hidden' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 0, minWidth: 0 }}>
                  {dotStatus && <StatusDot status={dotStatus} />}
                  {p.roster_position && <span style={{ fontSize: 9, fontWeight: 600, color: dynastyTheme.tokens.colors.neutral[500], marginRight: 3, flexShrink: 0 }}>{p.roster_position.replace(/_\d+$/, '')}</span>}
                  <MobilePlayerLink
                    mlbId={p.player_id}
                    name={p.name}
                    onClick={onPlayerClick}
                    style={{ fontWeight: 500, color: sc === 'S' ? dynastyTheme.tokens.colors.neutral[500] : dynastyTheme.tokens.colors.neutral[50], flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                  />
                  {p.is_probable_starter && <span style={{ marginLeft: 2, fontSize: 8, fontWeight: 700, padding: '0 2px', borderRadius: 2, color: C.white, backgroundColor: dynastyTheme.tokens.colors.info, flexShrink: 0 }}>SP</span>}
                </span>
              </td>
            );
          }
          if (col.key === 'game_status') {
            const gc = sc === 'I' ? dynastyTheme.tokens.colors.success : dynastyTheme.tokens.colors.neutral[500];
            const short = (p.game_status || '').length > 12 ? (p.game_status || '').substring(0, 12) + '…' : (p.game_status || '-');
            // Tap the Game cell → open that game's box score in the Games tab gamecast.
            if (p.game_pk && onOpenGame) {
              return (
                <td key={col.key} style={{ ...base, color: gc, fontSize: 10, textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() => onOpenGame(p.game_pk)}>{short}</td>
              );
            }
            return <td key={col.key} style={{ ...base, color: gc, fontSize: 10 }}>{short}</td>;
          }
          if (col.isRate) {
            // Locked rule (Item 1): player RATE column = PURE SEASON rate, recomputed
            // from emitted season COMPONENTS (never today's blended scalar). A 3-for-4
            // shows the season rate, not .750. Absent season → honest '-'.
            const v = seasonRate(col.abbrev, p.season);
            return <td key={col.key} style={{ ...base, color: dynastyTheme.tokens.colors.neutral[50] }}>{formatRate(col.abbrev, v)}</td>;
          }
          if (col.ctx) {
            const gameActive = sc === 'I' || sc === 'F';
            return <td key={col.key} style={{ ...base, color: gameActive ? dynastyTheme.tokens.colors.neutral[300] : dynastyTheme.tokens.colors.neutral[500] }}>{formatStat(p[col.key])}</td>;
          }
          const val = p[col.key];
          const isPos = typeof val === 'number' && val > 0;
          return <td key={col.key} style={{ ...base, color: isPos ? dynastyTheme.tokens.colors.success : dynastyTheme.tokens.colors.neutral[50] }}>{formatStat(val)}</td>;
        })}
      </tr>
    );
  };

  // Per-group column-header row (each status group carries its OWN column
  // headers right under its subheader — not a single standalone bar). Header
  // cells inside a tbody row use <td> styled as headers (valid + keeps the one
  // shared table so columns align + horizontal scroll + sticky first col work).
  const renderColumnHeaderRow = (gcode) => (
    <tr key={`ch-${gcode}`}>
      {cols.map(col => (
        <td key={col.key} style={{
          padding: col.sticky ? '4px 6px' : '3px 2px', textAlign: col.sticky ? 'left' : 'center',
          color: col.scoring ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[500],
          fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.3px',
          borderBottom: `1px solid ${mobileColors.divider}`, backgroundColor: C.surface[740],
          position: col.sticky ? 'sticky' : 'static', left: col.sticky ? 0 : 'auto',
          zIndex: col.sticky ? 2 : 0,
          ...(col.sticky ? { width: 112, minWidth: 112, maxWidth: 112 } : { minWidth: col.key === 'game_status' ? 54 : 30 }),
        }}>{col.label}</td>
      ))}
    </tr>
  );

  // Status subheader row spanning the full table width.
  const renderGroupHeader = (g) => (
    <tr key={`gh-${g.code}`}>
      <td colSpan={cols.length} style={{
        position: 'sticky', left: 0,
        padding: '5px 6px', backgroundColor: C.surface[740],
        borderBottom: `1px solid ${mobileColors.divider}`,
      }}>
        <span style={{ color: g.dot, fontSize: 10, marginRight: 5 }}>{g.glyph}</span>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase', color: dynastyTheme.tokens.colors.neutral[300] }}>
          {g.label}
        </span>
        <span style={{ fontSize: 9, color: dynastyTheme.tokens.colors.neutral[500], marginLeft: 5 }}>({g.rows.length})</span>
      </td>
    </tr>
  );

  return (
    <div>
      {allTeams.length > 1 && (
        <div style={{ padding: '6px 8px' }}>
          <select value={selectedTeamId || ''} onChange={e => setSelectedTeamId(e.target.value)} style={{
            width: '100%', padding: '5px 8px', backgroundColor: C.surface[820],
            color: dynastyTheme.tokens.colors.white, border: `1px solid ${C.coolGray[780]}`,
            borderRadius: 4, fontSize: 12,
          }}>
            {allTeams.map(t => <option key={t.team_id} value={t.team_id}>{t.team_name}{t.is_user_team ? ' (You)' : ''}</option>)}
          </select>
        </div>
      )}

      <div style={{ display: 'flex', borderBottom: `1px solid ${C.coolGray[780]}` }}>
        {['hitting', 'pitching'].map(t => (
          <div key={t} onClick={() => setSubTab(t)} style={{
            flex: 1, textAlign: 'center', padding: '7px 0', fontSize: 12, cursor: 'pointer',
            color: subTab === t ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[500],
            borderBottom: subTab === t ? `2px solid ${dynastyTheme.tokens.colors.primary}` : '2px solid transparent',
          }}>
            {t === 'hitting' ? `Hitters (${(data.hitting || []).length})` : `Pitchers (${(data.pitching || []).length})`}
          </div>
        ))}
      </div>

      <div className="home-scroll" style={hideScrollStyle}>
        {/* width:auto → columns size to content (snug), not stretched to fill. */}
        <table style={{ width: 'auto', borderCollapse: 'collapse', fontSize: 11 }}>
          {/* No standalone top header bar — each status group block is
              [subheader] → [its own column headers] → [its rows]; one Totals row
              across all groups at the very bottom. */}
          <tbody>
            {(() => {
              // Grouped by status; continuous zebra index across all groups.
              let gi = 0;
              return grouped.map((g) => (
                <React.Fragment key={`grp-${g.code}`}>
                  {renderGroupHeader(g)}
                  {renderColumnHeaderRow(g.code)}
                  {g.rows.map((p) => renderPlayerRow(p, gi++))}
                </React.Fragment>
              ));
            })()}
            {totals && (
              <tr style={{ backgroundColor: mobileTableStyles.totals.background, borderTop: mobileTableStyles.totals.border }}>
                {cols.map(col => (
                  <td key={col.key} style={{
                    padding: col.sticky ? '4px 6px' : '3px 2px', textAlign: col.sticky ? 'left' : 'center', fontWeight: 600,
                    color: dynastyTheme.tokens.colors.primary, fontSize: 11,
                    position: col.sticky ? 'sticky' : 'static', left: col.sticky ? 0 : 'auto',
                    zIndex: col.sticky ? 1 : 0,
                    backgroundColor: col.sticky ? mobileTableStyles.sticky.totals : 'transparent',
                    ...(col.sticky ? { width: 112, minWidth: 112, maxWidth: 112 } : {}),
                  }}>{/* Item 1: totals row = DAY throughout (backend day totals) */}
                    {col.sticky ? 'Totals' : (col.isRate ? formatRate(col.abbrev, totals[col.key]) : formatStat(totals[col.key]))}</td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// TAB 2: STANDINGS
// ============================================================================
const StandingsTab = ({ leagueId, userTeamId, refreshKey, onTeamClick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leagueId) return;
    leaguesAPI.getLiveScoringStandings(leagueId)
      .then(res => setData(res))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [leagueId, refreshKey]);

  if (loading && !data) return <div style={{ padding: 20, textAlign: 'center', color: dynastyTheme.tokens.colors.neutral[500], fontSize: 12 }}>Loading standings...</div>;
  if (!data?.teams) return <div style={{ padding: 20, textAlign: 'center', color: dynastyTheme.tokens.colors.neutral[500], fontSize: 12 }}>No standings data</div>;

  // Bible § 14: prefer backend-supplied categories (server-side derives from
  // league_settings.scoring_categories); fall back to canonical defaults from
  // statMapping.js rather than a hardcoded literal.
  const hitCats = data.categories?.hitting || DEFAULT_BATTING_STATS;
  const pitCats = data.categories?.pitching || DEFAULT_PITCHING_STATS;
  const allCats = [...hitCats, ...pitCats];
  const rateCats = new Set(['AVG', 'OBP', 'SLG', 'OPS', 'ERA', 'WHIP']);
  const reversedCats = new Set(['ERA', 'WHIP', 'BAA', 'BB/9']);
  const sorted = [...data.teams].sort((a, b) => (b.total_points || 0) - (a.total_points || 0));

  // Pre-today points and daily change computed by the backend
  // (correctly handles rate stats from raw cumulative components)

  const formatRaw = (cat, val) => {
    if (val == null || val === undefined) return '-';
    if (rateCats.has(cat)) return typeof val === 'number' ? val.toFixed(3).replace(/^0/, '') : val;
    return val;
  };

  const fmtPts = (v) => v === '-' || v == null ? '-' : v % 1 === 0 ? v : v.toFixed(1);

  // Per-category max points (the leader, possibly shared by N teams) and the
  // last (1 point) drive the cell color when there's no daily delta.
  const catMax = {};
  allCats.forEach((c) => {
    catMax[c] = Math.max(0, ...sorted.map((t) => (typeof t.category_points?.[c] === 'number' ? t.category_points[c] : 0)));
  });

  const GOLD = dynastyTheme.tokens.colors.primary;
  const MUTED = C.muted;
  const TH_BORDER = `1px solid ${C.border}`;
  const TR_BORDER = `1px solid ${C.hairline}`;
  const DIVIDER = C.surface.panelBlue;

  // Category POINTS color, in priority order (per locked spec).
  const ptsColor = (cat, pts, gained, lost) => {
    if (gained) return { color: GOLD, textShadow: `0 0 7px ${withAlpha(C.goldBase, 0.55)}` };
    if (lost) return { color: C.error, textShadow: `0 0 7px ${withAlpha(C.error, 0.55)}` };
    if (pts !== '-' && catMax[cat] > 1 && pts === catMax[cat]) return { color: C.spark.up };
    if (pts === 1) return { color: C.error };
    return { color: C.white };
  };

  const renderStatCell = (team, cat) => {
    const pts = team.category_points?.[cat] ?? '-';
    const raw = team.stats?.[cat];
    const pre = team.pre_today_category_points?.[cat];
    const gained = pre != null && pts !== '-' && pts > pre;
    const lost = pre != null && pts !== '-' && pts < pre;
    const c = ptsColor(cat, pts, gained, lost);
    return (
      <td key={cat} style={{ textAlign: 'center', padding: '7px 5px' }}>
        <div style={{ fontSize: 15, fontWeight: 500, lineHeight: 1, color: c.color, textShadow: c.textShadow }}>{fmtPts(pts)}</div>
        <div style={{ fontSize: 9, color: MUTED, lineHeight: 1, marginTop: 3 }}>{formatRaw(cat, raw)}</div>
      </td>
    );
  };

  const catTh = (c, isPts = false) => (
    <th key={isPts ? 'PTS' : c} style={{
      padding: '7px 5px', textAlign: 'center', color: GOLD,
      fontSize: isPts ? 10 : 9, fontWeight: isPts ? 900 : 800,
      backgroundColor: C.ground.screen, borderBottom: TH_BORDER,
      position: 'sticky', top: 0, zIndex: 1,
    }}>{isPts ? 'PTS' : c}</th>
  );

  return (
    <div className="home-scroll" style={{ ...hideScrollStyle, backgroundColor: C.ground.screen }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
        <thead>
          <tr>
            <th style={{
              textAlign: 'left', padding: '7px 8px', fontSize: 9, fontWeight: 800,
              textTransform: 'uppercase', color: MUTED, backgroundColor: C.ground.screen,
              borderBottom: TH_BORDER, position: 'sticky', left: 0, top: 0, zIndex: 3,
              width: 132, minWidth: 132, maxWidth: 132,
            }}>Team</th>
            {hitCats.map(c => catTh(c))}
            <th style={{ width: 2, padding: 0, backgroundColor: DIVIDER }} />
            {pitCats.map(c => catTh(c))}
            {catTh(null, true)}
          </tr>
        </thead>
        <tbody>
          {sorted.map((team, idx) => {
            const isUser = team.team_id === userTeamId;
            const pts = team.total_points || 0;
            const dailyChange = team.daily_change ?? 0;
            return (
              <tr key={team.team_id} style={{ borderBottom: TR_BORDER }}>
                {/* Frozen Team cell \u2014 ONLY the name cell carries the your-team tint
                    + left rail; stat cells stay on the normal dark ground. */}
                <td style={{
                  padding: '9px 8px', width: 132, maxWidth: 132, textAlign: 'left',
                  position: 'sticky', left: 0, zIndex: 1,
                  backgroundColor: isUser ? C.surface[830] : C.ground.screen,
                  borderLeft: `3px solid ${isUser ? GOLD : 'transparent'}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, minWidth: 0 }}>
                    <span style={{ fontSize: 9, color: MUTED, flexShrink: 0 }}>{idx + 1}</span>
                    <span onClick={() => onTeamClick && onTeamClick(team.team_id)} style={{
                      fontSize: 12, fontWeight: isUser ? 900 : 600,
                      color: isUser ? GOLD : C.white,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer',
                    }}>{team.team_name}</span>
                  </div>
                </td>
                {hitCats.map(c => renderStatCell(team, c))}
                <td style={{ width: 2, padding: 0, backgroundColor: DIVIDER }} />
                {pitCats.map(c => renderStatCell(team, c))}
                {/* PTS cell \u2014 total over daily +/- */}
                <td style={{ textAlign: 'center', padding: '7px 5px' }}>
                  <div style={{ fontSize: 16, fontWeight: 500, color: GOLD, lineHeight: 1 }}>{fmtPts(pts)}</div>
                  <div style={{ fontSize: 9, fontWeight: 700, lineHeight: 1, marginTop: 3, color: dailyChange === 0 ? C.coolGray[680] : dailyChange > 0 ? C.txn.add.stroke : C.spark.down }}>
                    {dailyChange === 0 ? '\u2014' : (dailyChange > 0 ? '+' : '') + dailyChange.toFixed(1)}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// TAB 3: GAMES (with inline Gamecast)
// ============================================================================
const GamesTab = ({ leagueId, userTeamId, userTeamName = null, onPlayerClick, initialGameId = null, refreshKey }) => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGamePk, setSelectedGamePk] = useState(null);
  const [gameDetail, setGameDetail] = useState(null);
  const [gcLoading, setGcLoading] = useState(false);
  const [gcTab, setGcTab] = useState('box');
  const panelRef = useRef(null);
  const [panelHeight, setPanelHeight] = useState(400);

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!leagueId) return;
    leaguesAPI.getLiveScoringGames(leagueId)
      .then(res => {
        const g = res?.games || [];
        setGames(g);
        // Only auto-select on first load, not on refreshes
        if (!hasInitialized.current) {
          hasInitialized.current = true;
          // Deep-link only: open directly into a specific game's gamecast when
          // initialGameId is passed. Otherwise the RAIL is the default view (tap
          // a card to open its gamecast) — no auto-select.
          if (initialGameId) {
            const target = g.find(x => x.game_pk === initialGameId);
            if (target) { selectGame(target.game_pk); return; }
          }
        } else if (selectedGamePk) {
          // On refresh, re-fetch the currently selected game's detail
          leaguesAPI.getGameDetail(leagueId, selectedGamePk)
            .then(res => setGameDetail(res))
            .catch(() => {});
        }
      })
      .catch(() => setGames([]))
      .finally(() => setLoading(false));
  }, [leagueId, refreshKey]);

  // Measure panel height
  useEffect(() => {
    const measure = () => {
      if (panelRef.current) {
        const rect = panelRef.current.getBoundingClientRect();
        const available = window.innerHeight - rect.top - 80;
        setPanelHeight(Math.max(200, available));
      }
    };
    const timer = setTimeout(measure, 200);
    window.addEventListener('resize', measure);
    return () => { clearTimeout(timer); window.removeEventListener('resize', measure); };
  }, [selectedGamePk, gcLoading]);

  const selectGame = useCallback((gamePk) => {
    setSelectedGamePk(gamePk);
    setGcLoading(true);
    setGcTab('box');
    leaguesAPI.getGameDetail(leagueId, gamePk)
      .then(res => setGameDetail(res))
      .catch(() => setGameDetail(null))
      .finally(() => setGcLoading(false));
  }, [leagueId]);

  // Gamecast polling for live games
  useEffect(() => {
    if (!selectedGamePk || !gameDetail?.game_info) return;
    if (gameDetail.game_info.status_code !== 'I') return;
    const interval = setInterval(() => {
      leaguesAPI.getGameDetail(leagueId, selectedGamePk)
        .then(res => setGameDetail(res))
        .catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, [selectedGamePk, leagueId, gameDetail?.game_info?.status_code]);

  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: C.coolGray[550], fontSize: 12 }}>Loading games...</div>;

  const gi = gameDetail?.game_info;
  const isPreGame = gi && gi.status_code === 'S';
  const selectedGame = games.find(g => g.game_pk === selectedGamePk);

  return (
    <div>
      {/* Horizontal game-card rail — ALWAYS visible (flush, hidden scrollbar,
          inline › affordance). Tapping a card expands its gamecast INLINE BELOW
          (the rail stays); the selected card is gold-bordered. */}
      <div className="home-scroll" style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '6px 8px', scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch', alignItems: 'stretch' }}>
        {games.map(game => {
          const sc = game.status_code;
          const isSelected = game.game_pk === selectedGamePk;
          const statusColor = sc === 'I' ? dynastyTheme.tokens.colors.success : (sc === 'F' || sc === 'O') ? dynastyTheme.tokens.colors.primary : C.muted;
          return (
            <div key={game.game_pk} onClick={() => selectGame(game.game_pk)} style={{
              backgroundColor: C.ground.screenTop, minWidth: 150, maxWidth: 150, padding: '6px 8px', flexShrink: 0, cursor: 'pointer',
              border: `1px solid ${isSelected ? dynastyTheme.tokens.colors.primary : C.border}`,
            }}>
              <div style={{ textAlign: 'center', fontSize: 9, color: statusColor, marginBottom: 3 }}>
                <GameDot statusCode={sc} />{getGameStatusText(game)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.textHi }}>{game.away_team}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: game.away_score != null ? dynastyTheme.tokens.colors.primary : C.coolGray[720] }}>{game.away_score ?? '-'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.textHi }}>{game.home_team}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: game.home_score != null ? dynastyTheme.tokens.colors.primary : C.coolGray[720] }}>{game.home_score ?? '-'}</span>
              </div>
              {(game.away_pitcher || game.home_pitcher) && (
                <div style={{ borderTop: `0.5px solid ${C.surface[880]}`, marginTop: 3, paddingTop: 3 }}>
                  {[game.away_pitcher, game.home_pitcher].map((p, pi) => p && (
                    <div key={pi} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: pi === 0 ? 2 : 0 }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                        <MobilePlayerLink
                          mlbId={p.mlb_id}
                          name={p.name || 'TBD'}
                          onClick={onPlayerClick}
                          style={{ fontSize: 9, color: C.coolGray[250] }}
                        />
                      </span>
                      <span style={{ marginLeft: 4, flexShrink: 0 }}>
                        <OwnerBadge isYours={p.team_id === userTeamId} team={p.is_free_agent ? null : p.team_name} />
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {games.length > 0 && (
          <div aria-hidden="true" style={{ alignSelf: 'center', color: C.muted, fontSize: 16, flexShrink: 0, padding: '0 2px' }}>›</div>
        )}
      </div>

      {/* Inline gamecast — expands BELOW the rail (the rail stays visible); NOT a
          route change, NOT a full-screen takeover. A collapse control closes it
          back to rail-only. Flush panel (no rounded card). */}
      {selectedGamePk && (
      <div ref={panelRef} style={{ padding: '0 8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0 4px' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.4px' }}>Gamecast</span>
          <button
            onClick={() => { setSelectedGamePk(null); setGameDetail(null); }}
            aria-label="Collapse gamecast"
            style={{ display: 'flex', alignItems: 'center', gap: 3, background: 'transparent', border: 'none', cursor: 'pointer', color: dynastyTheme.tokens.colors.primary, fontSize: 11, fontWeight: 700, padding: 0 }}
          >
            Collapse ⌄
          </button>
        </div>
        <div className="home-scroll" style={{
          backgroundColor: C.ground.screenTop, border: `1px solid ${C.border}`, padding: '10px',
          maxHeight: panelHeight, overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none',
        }}>
          {gcLoading ? (
            <div style={{ padding: 30, textAlign: 'center', color: C.coolGray[550], fontSize: 12 }}>Loading gamecast...</div>
          ) : !gi ? (
            <div style={{ padding: 30, textAlign: 'center', color: C.coolGray[550], fontSize: 12 }}>Loading game…</div>
          ) : (
            <>
              {/* Score header */}
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: C.textHi }}>{gi.away_team}</span>
                <span style={{ fontSize: 28, fontWeight: 800, color: dynastyTheme.tokens.colors.primary }}>{gi.away_score ?? 0}</span>
                <span style={{ fontSize: 12, color: C.coolGray[720] }}>-</span>
                <span style={{ fontSize: 28, fontWeight: 800, color: dynastyTheme.tokens.colors.primary }}>{gi.home_score ?? 0}</span>
                <span style={{ fontSize: 18, fontWeight: 700, color: C.textHi }}>{gi.home_team}</span>
              </div>
              <div style={{ textAlign: 'center', fontSize: 10, color: gi.status_code === 'I' ? dynastyTheme.tokens.colors.success : C.coolGray[550], marginBottom: 8 }}>
                {gi.status_code === 'I' && <StatusDot status="I" />}
                {getGameStatusText(gi)}
              </div>

              {/* Linescore */}
              {gameDetail.linescore?.innings?.length > 0 && (
                <div className="home-scroll" style={{ ...hideScrollStyle, marginBottom: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                    <thead><tr>
                      <th style={{ padding: '3px 6px', textAlign: 'left', color: C.coolGray[550], fontSize: 9, backgroundColor: C.surface[850] }} />
                      {gameDetail.linescore.innings.map(inn => {
                        const isCurrent = gi.status_code === 'I' && inn.num === gi.inning;
                        return <th key={inn.num} style={{ padding: '3px 4px', textAlign: 'center', color: isCurrent ? dynastyTheme.tokens.colors.primary : C.coolGray[550], fontWeight: isCurrent ? 700 : 400, fontSize: 9, backgroundColor: C.surface[850], minWidth: 20 }}>{inn.num}</th>;
                      })}
                      <th style={{ padding: '3px 4px', textAlign: 'center', color: dynastyTheme.tokens.colors.primary, fontSize: 9, fontWeight: 700, backgroundColor: C.surface[850] }}>R</th>
                      <th style={{ padding: '3px 4px', textAlign: 'center', color: C.coolGray[550], fontSize: 9, backgroundColor: C.surface[850] }}>H</th>
                      <th style={{ padding: '3px 4px', textAlign: 'center', color: C.coolGray[550], fontSize: 9, backgroundColor: C.surface[850] }}>E</th>
                    </tr></thead>
                    <tbody>
                      {['away', 'home'].map((side, si) => (
                        <tr key={side} style={{ backgroundColor: si === 0 ? mobileTableStyles.rows.even : mobileTableStyles.rows.odd }}>
                          <td style={{ padding: '3px 6px', fontWeight: 600, color: mobileColors.textSecondary, fontSize: 10 }}>{gi[`${side}_team`]}</td>
                          {gameDetail.linescore.innings.map(inn => (
                            <td key={inn.num} style={{ padding: '3px 4px', textAlign: 'center', color: C.textHi, fontSize: 10 }}>{inn[side] ?? '-'}</td>
                          ))}
                          <td style={{ padding: '3px 4px', textAlign: 'center', color: dynastyTheme.tokens.colors.primary, fontWeight: 700, fontSize: 11 }}>{gameDetail.linescore.totals?.[side]?.runs ?? '-'}</td>
                          <td style={{ padding: '3px 4px', textAlign: 'center', color: C.textHi, fontSize: 10 }}>{gameDetail.linescore.totals?.[side]?.hits ?? '-'}</td>
                          <td style={{ padding: '3px 4px', textAlign: 'center', color: C.textHi, fontSize: 10 }}>{gameDetail.linescore.totals?.[side]?.errors ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pre-game: probable pitchers + not started message */}
              {isPreGame && (
                <div style={{ padding: '10px 0' }}>
                  {(selectedGame?.away_pitcher || selectedGame?.home_pitcher) && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: dynastyTheme.tokens.colors.primary, marginBottom: 6 }}>Probable Pitchers</div>
                      <div style={{ borderTop: `0.5px solid ${C.surface[880]}`, paddingTop: 6 }}>
                        {[{ label: 'Away', p: selectedGame.away_pitcher }, { label: 'Home', p: selectedGame.home_pitcher }].map(({ label, p }) => p && (
                          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                            <span style={{ fontSize: 11, color: C.coolGray[650], width: 40 }}>{label}:</span>
                            <MobilePlayerLink
                              mlbId={p.mlb_id}
                              name={p.name || 'TBD'}
                              onClick={onPlayerClick}
                              style={{ fontSize: 12, fontWeight: 500, color: C.textHi }}
                            />
                            <OwnerBadge isYours={p.team_id === userTeamId} team={p.is_free_agent ? null : p.team_name} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ textAlign: 'center', color: C.coolGray[550], fontSize: 12 }}>
                    Game has not started
                  </div>
                </div>
              )}

              {/* Box / PBP tabs */}
              {!isPreGame && (
                <>
                  <div style={{ display: 'flex', borderBottom: `1px solid ${C.coolGray[780]}`, marginBottom: 6 }}>
                    {['box', 'pbp'].map(t => (
                      <div key={t} onClick={() => setGcTab(t)} style={{
                        flex: 1, textAlign: 'center', padding: '5px 0', fontSize: 11, cursor: 'pointer',
                        color: gcTab === t ? dynastyTheme.tokens.colors.primary : C.coolGray[550],
                        borderBottom: gcTab === t ? `2px solid ` : '2px solid transparent',
                      }}>{t === 'box' ? 'Box Score' : 'Play-by-Play'}</div>
                    ))}
                  </div>

                  {gcTab === 'box' ? (
                    <>
                      {['away', 'home'].map(side => {
                        const bat = gameDetail.batting?.[side];
                        const pit = gameDetail.pitching?.[side];
                        if (!bat && !pit) return null;
                        return (
                          <div key={side} style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: C.white, padding: '4px 6px', borderBottom: `1px solid ${C.coolGray[780]}` }}>{bat?.team || pit?.team || side} — Batting</div>
                            <div className="home-scroll" style={hideScrollStyle}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                                <thead><tr>
                                  {['Player', 'AB', 'R', 'H', 'RBI', 'BB', 'SO', 'AVG', 'OPS'].map(h => (
                                    <th key={h} style={{ padding: '3px 3px', textAlign: h === 'Player' ? 'left' : 'center', color: C.coolGray[550], fontSize: 9, position: h === 'Player' ? 'sticky' : 'static', left: h === 'Player' ? 0 : 'auto', zIndex: h === 'Player' ? 2 : 0, backgroundColor: C.surface[850], minWidth: h === 'Player' ? 90 : 24, paddingLeft: h === 'Player' ? 6 : 3 }}>{h}</th>
                                  ))}
                                </tr></thead>
                                <tbody>
                                  {(bat?.players || []).map((p, i) => {
                                    const owned = p.ownership?.fantasy_team;
                                    const isYours = !!owned && owned === userTeamName;
                                    const bg = i % 2 === 0 ? mobileTableStyles.rows.even : mobileTableStyles.rows.odd;
                                    return (
                                      <tr key={p.player_id || i} style={{ backgroundColor: bg }}>
                                        <td style={{ padding: '3px 3px', position: 'sticky', left: 0, zIndex: 1, backgroundColor: bg, paddingLeft: 6 }}>
                                          <MobilePlayerLink
                                            mlbId={p.player_id}
                                            name={p.name}
                                            onClick={onPlayerClick}
                                            style={{ color: isYours ? dynastyTheme.tokens.colors.primary : C.textHi, fontWeight: 500, fontSize: 10 }}
                                          />
                                          <span style={{ marginLeft: 3 }}><OwnerBadge isYours={isYours} team={isYours ? null : (owned || null)} /></span>
                                        </td>
                                        {['ab', 'r', 'h', 'rbi', 'bb', 'so'].map(k => <td key={k} style={{ padding: '3px 3px', textAlign: 'center', color: C.textHi, fontSize: 10 }}>{p[k] ?? '-'}</td>)}
                                        <td style={{ padding: '3px 3px', textAlign: 'center', color: C.textHi, fontSize: 10 }}>{formatStat(p.avg)}</td>
                                        <td style={{ padding: '3px 3px', textAlign: 'center', color: C.textHi, fontSize: 10 }}>{formatStat(p.ops)}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            {(bat?.batting_notes?.length > 0 || bat?.fielding_notes?.length > 0) && (
                              <div style={{ padding: '3px 6px' }}>
                                {(bat?.batting_notes || []).map((note, i) => (
                                  <div key={`bn${i}`} style={{ fontSize: 9, color: C.coolGray[550], lineHeight: 1.4 }}>
                                    {typeof note === 'string' ? note : `${note.label || ''} — ${note.value || ''}`}
                                  </div>
                                ))}
                                {(bat?.fielding_notes || []).map((note, i) => (
                                  <div key={`fn${i}`} style={{ fontSize: 9, color: C.coolGray[550], lineHeight: 1.4 }}>
                                    {typeof note === 'string' ? note : `${note.label || ''} — ${note.value || ''}`}
                                  </div>
                                ))}
                              </div>
                            )}
                            <div style={{ fontSize: 11, fontWeight: 600, color: C.white, padding: '4px 6px', borderBottom: `1px solid ${C.coolGray[780]}`, marginTop: 4 }}>{pit?.team || side} — Pitching</div>
                            <div className="home-scroll" style={hideScrollStyle}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                                <thead><tr>
                                  {['Pitcher', 'IP', 'H', 'ER', 'BB', 'SO', 'HR', 'ERA', 'WHIP'].map(h => (
                                    <th key={h} style={{ padding: '3px 3px', textAlign: h === 'Pitcher' ? 'left' : 'center', color: C.coolGray[550], fontSize: 9, position: h === 'Pitcher' ? 'sticky' : 'static', left: h === 'Pitcher' ? 0 : 'auto', zIndex: h === 'Pitcher' ? 2 : 0, backgroundColor: C.surface[850], minWidth: h === 'Pitcher' ? 90 : 24, paddingLeft: h === 'Pitcher' ? 6 : 3 }}>{h}</th>
                                  ))}
                                </tr></thead>
                                <tbody>
                                  {(pit?.players || []).map((p, i) => {
                                    const owned = p.ownership?.fantasy_team;
                                    const isYours = !!owned && owned === userTeamName;
                                    const bg = i % 2 === 0 ? mobileTableStyles.rows.even : mobileTableStyles.rows.odd;
                                    return (
                                      <tr key={p.player_id || i} style={{ backgroundColor: bg }}>
                                        <td style={{ padding: '3px 3px', position: 'sticky', left: 0, zIndex: 1, backgroundColor: bg, paddingLeft: 6 }}>
                                          <MobilePlayerLink
                                            mlbId={p.player_id}
                                            name={p.name}
                                            onClick={onPlayerClick}
                                            style={{ color: isYours ? dynastyTheme.tokens.colors.primary : C.textHi, fontWeight: 500, fontSize: 10 }}
                                          />
                                          {p.decision && <span style={{ marginLeft: 3, fontSize: 9, color: dynastyTheme.tokens.colors.primary, fontWeight: 600 }}>({p.decision})</span>}
                                          <span style={{ marginLeft: 3 }}><OwnerBadge isYours={isYours} team={isYours ? null : (owned || null)} /></span>
                                        </td>
                                        {['ip', 'h', 'er', 'bb', 'so', 'hr'].map(k => <td key={k} style={{ padding: '3px 3px', textAlign: 'center', color: C.textHi, fontSize: 10 }}>{p[k] ?? '-'}</td>)}
                                        <td style={{ padding: '3px 3px', textAlign: 'center', color: C.textHi, fontSize: 10 }}>{formatStat(p.era)}</td>
                                        <td style={{ padding: '3px 3px', textAlign: 'center', color: C.textHi, fontSize: 10 }}>{formatStat(p.whip)}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            {pit?.pitching_notes?.length > 0 && (
                              <div style={{ padding: '3px 6px' }}>
                                {pit.pitching_notes.map((note, i) => (
                                  <div key={`pn${i}`} style={{ fontSize: 9, color: C.coolGray[550], lineHeight: 1.4 }}>
                                    {typeof note === 'string' ? note : `${note.label || ''} — ${note.value || ''}`}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <div>
                      {(gameDetail.plays || []).slice().reverse().map((play, i) => (
                        <div key={i} style={{ padding: '6px 8px', borderBottom: `0.5px solid ${C.surface[880]}`, fontSize: 11, color: C.coolGray[250] }}>
                          <span style={{ color: C.coolGray[550], fontSize: 9, marginRight: 6 }}>{play.half_inning === 'top' ? '▲' : '▼'}{play.inning}</span>
                          {play.description}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

// ============================================================================
// TAB 4: ALL TEAMS
// ============================================================================
const AllTeamsTab = ({ leagueId, userTeamId, refreshKey, onTeamClick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leagueId) return;
    leaguesAPI.getAllTeamsLiveStats(leagueId)
      .then(res => setData(res))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [leagueId, refreshKey]);

  if (loading && !data) return <div style={{ padding: 20, textAlign: 'center', color: dynastyTheme.tokens.colors.neutral[500], fontSize: 12 }}>Loading...</div>;
  if (!data?.teams) return <div style={{ padding: 20, textAlign: 'center', color: dynastyTheme.tokens.colors.neutral[500], fontSize: 12 }}>No data</div>;

  const renderTable = (title, statKey, cols) => (
    <div>
      <div style={{ padding: '5px 8px', fontSize: 11, fontWeight: 600, color: dynastyTheme.tokens.colors.primary, borderBottom: `1px solid ${C.coolGray[780]}` }}>{title}</div>
      <div className="home-scroll" style={hideScrollStyle}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
          <thead><tr>
            {cols.map(col => (
              <th key={col.key} style={{
                padding: '4px 3px', textAlign: col.key === 'name' ? 'left' : 'center',
                color: col.scoring ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[500],
                fontWeight: 500, fontSize: 9, borderBottom: `1px solid ${C.coolGray[780]}`,
                backgroundColor: C.surface[850],
                position: col.key === 'name' ? 'sticky' : 'static', left: col.key === 'name' ? 0 : 'auto',
                zIndex: col.key === 'name' ? 2 : 0, minWidth: col.key === 'name' ? 110 : 28,
                paddingLeft: col.key === 'name' ? 6 : 3,
              }}>{col.label}</th>
            ))}
          </tr></thead>
          <tbody>
            {data.teams.map((team, idx) => {
              const isUser = team.team_id === userTeamId;
              const bg = isUser ? mobileTableStyles.userTeamRow.background : idx % 2 === 0 ? mobileTableStyles.rows.even : mobileTableStyles.rows.odd;
              const stats = team[statKey] || {};
              return (
                <tr key={team.team_id} style={{ backgroundColor: bg }}>
                  {cols.map(col => {
                    if (col.key === 'name') return (
                      <td key={col.key}
                          onClick={() => onTeamClick && onTeamClick(team.team_id)}
                          style={{ padding: '4px 3px', position: 'sticky', left: 0, zIndex: 1, backgroundColor: bg, paddingLeft: 6, fontWeight: 500, color: isUser ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[50], fontSize: 11, cursor: onTeamClick ? 'pointer' : 'default', textDecoration: 'none' }}>{team.team_name}</td>
                    );
                    const val = stats[col.key];
                    const isPos = col.scoring && typeof val === 'number' && val > 0;
                    return <td key={col.key} style={{ padding: '4px 3px', textAlign: 'center', color: isPos ? dynastyTheme.tokens.colors.success : dynastyTheme.tokens.colors.neutral[50], fontSize: 10 }}>{formatStat(val)}</td>;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const hitCols = [
    { key: 'name', label: 'Team' },
    { key: 'ab', label: 'AB' }, { key: 'h', label: 'H' }, { key: 'bb', label: 'BB' },
    { key: 'r', label: 'R', scoring: true }, { key: 'hr', label: 'HR', scoring: true },
    { key: 'rbi', label: 'RBI', scoring: true }, { key: 'sb', label: 'SB', scoring: true },
    { key: 'avg', label: 'AVG', scoring: true },
  ];
  const pitCols = [
    { key: 'name', label: 'Team' },
    { key: 'ip', label: 'IP' }, { key: 'h', label: 'H' }, { key: 'er', label: 'ER' }, { key: 'bb', label: 'BB' },
    { key: 'w', label: 'W', scoring: true }, { key: 'sv', label: 'SV', scoring: true },
    { key: 'era', label: 'ERA', scoring: true }, { key: 'whip', label: 'WHIP', scoring: true },
    { key: 'so', label: 'SO', scoring: true }, { key: 'qs', label: 'QS', scoring: true },
  ];

  return (
    <div>
      {renderTable("Today's Hitting", 'hitting', hitCols)}
      <div style={{ borderTop: `1px solid ${C.coolGray[780]}`, marginTop: 4 }} />
      {renderTable("Today's Pitching", 'pitching', pitCols)}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function MobileLiveScoring({ leagueId, initialGameId = null }) {
  const [activeTab, setActiveTab] = useState(initialGameId ? 'games' : 'team');
  const [allTeams, setAllTeams] = useState([]);
  const [userTeamId, setUserTeamId] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [teamOverride, setTeamOverride] = useState(null);
  // Deep-link: a game_pk to open in the Games tab's gamecast (from a Game-cell tap on
  // the team-stats table). GamesTab remounts on tab switch and opens its initialGameId.
  const [gameOverride, setGameOverride] = useState(null);

  // Player modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCardOpen, setIsCardOpen] = useState(false);
  const [activePlayerId, setActivePlayerId] = useState(null);

  useEffect(() => {
    if (!leagueId) return;
    leaguesAPI.getLeagueTeams(leagueId)
      .then(res => {
        if (res?.success && res.teams) {
          setAllTeams(res.teams.map(t => ({
            team_id: t.team_id, team_name: t.team_name || 'Team',
            is_user_team: t.is_user_team || false,
          })));
          const user = res.teams.find(t => t.is_user_team);
          if (user) setUserTeamId(user.team_id);
        }
      })
      .catch(() => {});
  }, [leagueId]);

  // Auto-refresh timer display
  useEffect(() => {
    const interval = setInterval(() => setSecondsAgo(Math.floor((Date.now() - lastRefresh) / 1000)), 1000);
    return () => clearInterval(interval);
  }, [lastRefresh]);

  // Auto-refresh data
  useEffect(() => {
    const interval = setInterval(() => setLastRefresh(Date.now()), REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  const handlePlayerClick = useCallback((mlbId) => {
    if (mlbId) { setActivePlayerId(mlbId.toString()); setIsModalOpen(true); }
  }, []);

  const handleTeamClick = useCallback((teamId) => {
    setTeamOverride({ teamId, ts: Date.now() });
    setActiveTab('team');
  }, []);

  // Open a specific game's box score (Games tab gamecast) from a team-stats Game cell.
  const handleGameClick = useCallback((gamePk) => {
    if (!gamePk) return;
    setGameOverride(gamePk);
    setActiveTab('games');
  }, []);

  const tabs = [
    { id: 'team', label: 'My Team' },
    { id: 'standings', label: 'Standings' },
    { id: 'games', label: 'Games' },
    { id: 'allteams', label: 'All Teams' },
  ];

  return (
    <div style={{ backgroundColor: 'transparent' }}>
      <style>{`.home-scroll::-webkit-scrollbar { display: none; }`}</style>

      {/* Main tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${C.coolGray[780]}`, backgroundColor: 'transparent', marginBottom: '8px' }}>
        {tabs.map(tab => (
          <div key={tab.id} onClick={() => { setActiveTab(tab.id); setGameOverride(null); }} style={{
            flex: 1, textAlign: 'center', padding: '6px 4px', fontSize: 12, cursor: 'pointer',
            color: activeTab === tab.id ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[500],
            borderBottom: activeTab === tab.id ? `2px solid ${dynastyTheme.tokens.colors.primary}` : '2px solid transparent',
          }}>{tab.label}</div>
        ))}
      </div>

      {/* Tab content — flush, edge-to-edge on the dark ground (no rounded card). */}
      <div>
        {activeTab === 'team' && <MyTeamTab leagueId={leagueId} allTeams={allTeams} userTeamId={userTeamId} onPlayerClick={handlePlayerClick} onOpenGame={handleGameClick} refreshKey={lastRefresh} overrideTeamId={teamOverride} />}
        {activeTab === 'standings' && <StandingsTab leagueId={leagueId} userTeamId={userTeamId} refreshKey={lastRefresh} onTeamClick={handleTeamClick} />}
        {activeTab === 'games' && <GamesTab leagueId={leagueId} userTeamId={userTeamId} userTeamName={allTeams.find(t => t.team_id === userTeamId)?.team_name || null} onPlayerClick={handlePlayerClick} initialGameId={gameOverride ?? initialGameId} refreshKey={lastRefresh} />}
        {activeTab === 'allteams' && <AllTeamsTab leagueId={leagueId} userTeamId={userTeamId} refreshKey={lastRefresh} onTeamClick={handleTeamClick} />}
      </div>

      {/* Updated timestamp */}
      <div style={{ fontSize: 10, color: dynastyTheme.tokens.colors.neutral[600], textAlign: 'center', padding: '6px 0' }}>
        Auto-refreshes every 30s · Updated {secondsAgo}s ago
      </div>

      {/* Player Modal */}
      <MobilePlayerModal playerId={activePlayerId} isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setTimeout(() => setActivePlayerId(null), 300); }} onExpandToCard={() => { setIsModalOpen(false); setIsCardOpen(true); }} leagueId={leagueId} />
      <MobilePlayerCard playerId={activePlayerId} leagueId={leagueId} isOpen={isCardOpen} onClose={() => { setIsCardOpen(false); setIsModalOpen(false); setTimeout(() => setActivePlayerId(null), 300); }} onCollapse={() => { setIsCardOpen(false); setIsModalOpen(true); }} />
    </div>
  );
}

export default MobileLiveScoring;
